'use client';

import { cn } from '../_lib/utils';
import { Title } from '@radix-ui/react-dialog';
import { Popup } from '../Popup';
import type { SanityImage } from '../types';
import { Media } from '../Media';
import { Text } from '../Text';
import { IconResolver, type IconType } from '../IconResolver';
import { Card, type CardProps } from '../Card';
import { Panel } from '../Panel';
import { ButtonLink } from '../Inputs/Button';
import { useBrandValue } from '../Theme';
import type { ReactNode } from 'react';

export type ProfileCardProps = {
	className?: string;
	type?: 'link' | 'popup';
	image?: SanityImage;
	label?: string;
	name?: string;
	jobTitle?: string;
	shortBio?: ReactNode;
	link?: {
		href?: string;
		icon?: IconType;
	};
	content?: Omit<CardProps, 'layout' | 'size'>[];
	translations?: {
		bio?: string;
		viewBio?: string;
		featuredIn?: string;
	};
};

export function ProfileCard(props: ProfileCardProps) {
	const currentBrand = useBrandValue();

	const isPopup = props.type === undefined || props.type === 'popup';

	return (
		<article className={cn('relative', isPopup ? 'group' : '', props.className)} data-component='ProfileCard'>
			<div className={`relative flex isolate rounded-lg overflow-hidden aspect-3/4 bg-purple-10`}>
				{props.image && (
					<Media
						className='@container-normal! absolute -z-10 h-full top-0 left-0 [&_img]:group-hover:scale-105 [&_img]:transition-transform [&_img]:duration-fast [&_img]:w-full [&_img]:h-full'
						image={props.image}
					/>
				)}

				<div
					className='absolute -z-10 h-full w-full top-0 left-0 pointer-events-none'
					style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 42.16%, rgba(0, 0, 0, 0.4) 100%)' }}
				/>

				{isPopup ? (
					<Popup
						brand={currentBrand}
						layout='sidebar-slim'
						triggerButton={
							<button className='w-full p-spacing-md mt-auto flex items-end justify-between gap-spacing-md text-text-white before:inset-x-0 before:inset-y-0 before:absolute'>
								<Title asChild>
									<ProfileCardTitle isPopup={isPopup} name={props.name} jobTitle={props.jobTitle} translations={props.translations} />
								</Title>

								<div className='flex items-center justify-center size-10 flex-none bg-button-primary-background rounded-full transition-transform duration-fast group-hover:scale-110'>
									<IconResolver icon='plus' className='w-3 h-3' />
								</div>
							</button>
						}
					>
						<Profile {...props} />
					</Popup>
				) : (
					<div className='w-full p-spacing-md mt-auto flex items-end justify-between gap-spacing-md text-text-white'>
						<ProfileCardTitle isPopup={isPopup} name={props.name} jobTitle={props.jobTitle} translations={props.translations} />

						{props.link && (
							<ButtonLink
								data-theme='dark'
								// @TODO: We probably should add "invert" button styles (just like in kensa) to avoid having to use "data-theme='dark'"
								className='border-button-tertiary-border'
								parent='unused'
								href={props.link.href}
								iconOnly
								styleSize='medium'
								styleType='tertiary'
							>
								<IconResolver icon={props.link.icon} className='w-3.5 h-3.5' />
							</ButtonLink>
						)}
					</div>
				)}
			</div>
		</article>
	);
}

function ProfileCardTitle(props: { isPopup: boolean } & Pick<ProfileCardProps, 'name' | 'jobTitle' | 'translations'>) {
	const viewBioText = props.translations?.viewBio ?? 'View bio';

	return (
		<div className='relative text-left transition-all duration-fast group-hover:-translate-y-6'>
			{props.name && (
				<Text as='h3' className='mb-spacing-3xs md:text-heading-2xs' styleType='heading-4xs'>
					{props.name}
				</Text>
			)}

			{props.jobTitle && <Text styleType='heading-6xs'>{props.jobTitle}</Text>}

			<Text
				aria-hidden={!props.isPopup}
				className='absolute bottom-0 left-0 opacity-0 translate-y-6 transition-all duration-fast group-hover:opacity-100'
				styleType='heading-6xs'
			>
				{viewBioText}
			</Text>
		</div>
	);
}

export function Profile(props: ProfileCardProps) {
	const bioText = props.translations?.bio ?? 'Bio';
	const featuredIn = props.translations?.featuredIn ?? 'Featured in';

	return (
		<Panel
			label={props.label}
			title={props.name}
			image={props.image}
			meta={props.jobTitle ? [props.jobTitle] : undefined}
			description={{
				label: bioText,
				copy: props.shortBio,
			}}
			headerChildren={
				props.link && (
					<ButtonLink parent='unused' href={props.link.href} iconOnly styleSize='medium' styleType='tertiary'>
						<IconResolver icon={props.link.icon} className='w-3.5 h-3.5' />
					</ButtonLink>
				)
			}
		>
			{props.content && props.content.length > 0 && (
				<div>
					<Text className='mb-responsive-xs text-text-primary' styleType='label-md'>
						{featuredIn}
					</Text>

					<div className='flex flex-col gap-responsive-xs'>
						{props.content.map((item, i) => (
							<Card key={`${item.title}-${i}-profile-card`} layout='horizontal' size='md' {...item} />
						))}
					</div>
				</div>
			)}
		</Panel>
	);
}
